import React from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, ChevronLeft, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LookupField } from '@/components/shared/lookup-field';
import { toDateInputValue, parseNzWallToUtc } from '@/lib/format-date';
import { UploadField } from '@/components/shared/crud-page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

// Finalize now writes status 'Complete' for both paths (direct + IoT-provider).
// Legacy 'completed'/'awaiting_integration' are still recognised so records
// finalized before the migration keep routing to the Finalize step.
const isFinalizedStatus = (status: string | null | undefined): boolean =>
  status === 'Complete' || status === 'completed' || status === 'awaiting_integration';

// ---------- Types mirroring the /wizard-state response ----------

interface PersonRef {
  _id: string;
  fullName?: string | null;
  email?: string | null;
  /** Populated department (Zoho-parity: each person is shown with their
   *  department alongside their name on the task assignment strip). */
  department?: { _id: string; name?: string | null } | null;
}

interface Task {
  _id: string;
  pointName: string | null;
  pointDescription: string | null;
  stage: string | null;
  fieldType: 'checkbox' | 'text' | 'textarea' | 'number' | 'date' | 'select' | 'ref' | 'image';
  fieldOptions: string[];
  refEndpoint: string | null;
  refLabel: string | null;
  required: boolean;
  /** Legacy column on OnboardAsset that mirrors this task's value
   *  (e.g. `fleet`, `registration`). Used to back-fill the form from
   *  records imported under the old BUILTIN_FIELDS flow. */
  sourceField: string | null;
  assetField: string | null;
  responsiblePerson: PersonRef | null;
  otherStaff: PersonRef[];
}

interface Stage { name: string; tasks: Task[] }

interface WizardState {
  onboardAsset: any;
  isFallback: boolean;
  settingId?: string;
  stages: Stage[];
  answers: Record<string, any>;
  currentStageIndex: number;
}

// The wizard is fully task-driven — there's no hardcoded "Stage 1" of
// asset-identity fields anymore. The 13 fields that used to live here
// (Purchase Order #, Fleet, Make, Model, Year, Colour, Photo, Reg, VIN,
// Engine, Serial, IoT Provider, Comments) are now real
// AssetSettingTask records under a stage like "Asset Identity", so
// admins can assign a responsiblePerson + otherStaff to each one and
// reorder them like any other task. See `seed-onboard-identity-tasks.ts`
// for the one-off seed that creates them on the active config.

// ---------- Stepper ----------

type StepStatus = 'done' | 'current' | 'locked';

function Stepper({
  steps, currentIndex, maxReachedIndex, onJump,
}: {
  steps: string[];
  currentIndex: number;
  maxReachedIndex: number;
  onJump: (i: number) => void;
}) {
  return (
    <ol className="flex flex-wrap items-center gap-1 py-3 px-4 border-b bg-muted/30">
      {steps.map((label, i) => {
        let status: StepStatus;
        if (i < currentIndex) status = 'done';
        else if (i === currentIndex) status = 'current';
        else status = 'locked';
        // Allow jumping back to any visited stage; future stages stay locked
        // until the linear gate clears them.
        const canJump = i <= maxReachedIndex;
        return (
          <li key={label + i} className="flex items-center">
            <button
              type="button"
              onClick={() => canJump && onJump(i)}
              disabled={!canJump}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors',
                status === 'current' && 'bg-primary text-primary-foreground font-medium',
                status === 'done' && 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200',
                status === 'locked' && 'text-muted-foreground bg-muted/40 cursor-not-allowed',
                canJump && status !== 'current' && 'cursor-pointer',
              )}
            >
              <span className={cn(
                'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px]',
                status === 'current' && 'bg-primary-foreground text-primary',
                status === 'done' && 'bg-emerald-200 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100',
                status === 'locked' && 'bg-muted text-muted-foreground',
              )}>
                {status === 'done' ? <Check className="h-3 w-3" /> :
                 status === 'locked' ? <Lock className="h-3 w-3" /> : i + 1}
              </span>
              <span className="truncate max-w-[160px]">{label}</span>
            </button>
            {i < steps.length - 1 && (
              <span className="mx-1 text-muted-foreground">›</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------- Per-task field renderer ----------

/** Footer strip under each task showing who owns it. Renders inline so the
 *  user can see the responsible person + any extra staff alongside the
 *  task input. Hidden entirely when neither field is set on the config. */
function TaskAssignment({ task }: { task: Task }) {
  const personLabel = (p: PersonRef): string => p.fullName?.trim() || p.email || '—';
  const departmentLabel = (p: PersonRef): string | null => p.department?.name?.trim() || null;
  const renderPerson = (p: PersonRef, key?: string) => {
    const dept = departmentLabel(p);
    return (
      <span key={key} className="font-medium">
        {personLabel(p)}
        {dept && <span className="text-muted-foreground font-normal"> ({dept})</span>}
      </span>
    );
  };
  const hasResponsible = !!task.responsiblePerson;
  const hasOthers = (task.otherStaff?.length ?? 0) > 0;
  if (!hasResponsible && !hasOthers) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs pt-1 border-t mt-2">
      {hasResponsible && (
        <div>
          <span className="text-muted-foreground">Responsible: </span>
          {renderPerson(task.responsiblePerson!)}
        </div>
      )}
      {hasOthers && (
        <div>
          <span className="text-muted-foreground">Other staff: </span>
          {task.otherStaff.map((p, i) => (
            <React.Fragment key={p._id}>
              {i > 0 && <span>, </span>}
              {renderPerson(p)}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskField({ task, value, onChange, onCommit, disabled }: {
  task: Task;
  value: any;
  onChange: (v: any) => void;
  /** Persistence trigger — fired when the value is "stable" and ready to
   *  save: on blur for typed inputs, on the selection itself for
   *  checkbox / select / ref / date. Lets the wizard auto-save every
   *  field without flooding the server on every keystroke. */
  onCommit?: (v: any) => void;
  disabled?: boolean;
}) {
  // The ref case is rendered by `LookupField` from the shared lookup-field
  // module — it does its own fetching with the same query key shape, so
  // we no longer need a local `refOptions` query here.
  switch (task.fieldType) {
    case 'checkbox':
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <Checkbox
            checked={!!value}
            disabled={disabled}
            onCheckedChange={(v) => { const next = v === true; onChange(next); onCommit?.(next); }}
          />
          <span>{task.pointName || 'Confirmed'}</span>
        </label>
      );
    case 'text':
      return (
        <Input
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit?.(e.target.value)}
        />
      );
    case 'textarea':
      return (
        <Textarea
          rows={3}
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit?.(e.target.value)}
        />
      );
    case 'image':
      // Real upload widget — shares the same component the CRUD forms
      // use for image fields (Asset photo, Material image, etc.). The
      // task answer stores the S3 key; UploadField fetches a signed
      // preview URL on mount. `f.type='image'` + uploadPrefix tells
      // the widget where to put the file in S3. UploadField doesn't
      // accept a `disabled` prop directly, so for read-only / non-
      // assignee renders we wrap it in a pointer-events-none shell.
      return (
        <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
          <UploadField
            f={{ name: 'photo', label: 'Photo', type: 'image', uploadPrefix: 'asset' } as any}
            value={typeof value === 'string' ? value : null}
            onChange={(key) => { onChange(key); onCommit?.(key); }}
          />
        </div>
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          onBlur={(e) => onCommit?.(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'date':
      return (
        <Input
          type="date"
          value={value ? toDateInputValue(value) : ''}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value ? parseNzWallToUtc(`${e.target.value}T00:00`)?.toISOString() ?? null : null;
            onChange(v); onCommit?.(v);
          }}
        />
      );
    case 'select':
      return (
        <Select
          value={value ?? '__none__'}
          onValueChange={(v) => { const next = v === '__none__' ? null : v; onChange(next); onCommit?.(next); }}
          disabled={disabled}
        >
          <SelectTrigger><SelectValue placeholder="— Select —" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {(task.fieldOptions ?? []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'ref': {
      // Shared searchable lookup — big ref lists (e.g. /employees) need a
      // search input, and consistency across wizards / forms matters more
      // than the small bundle savings of the inline Select.
      return (
        <LookupField
          endpoint={task.refEndpoint ?? ''}
          labelKey={task.refLabel || 'name'}
          value={value}
          onChange={(v) => { onChange(v); onCommit?.(v); }}
          disabled={disabled}
        />
      );
    }
  }
}

// ---------- Wizard page ----------

export function OnboardWizardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const myEmployeeId = useAuthStore((s) => s.employee?.id);

  // A task is "mine" to answer only when I'm the explicitly-named responsible
  // person or in otherStaff. Mirrors the server's assertCanAnswerTask gate —
  // strict: an unassigned task is locked until an admin names someone.
  const isAssignedToMe = React.useCallback((t: Task): boolean => {
    if (!myEmployeeId) return false;
    if (t.responsiblePerson?._id === myEmployeeId) return true;
    return (t.otherStaff ?? []).some((s) => s._id === myEmployeeId);
  }, [myEmployeeId]);

  // Human-readable reason a task is locked for the current user — surfaces the
  // assignee names so the user knows who to chase, or that no one is assigned.
  function lockReasonFor(t: Task): string {
    if (!myEmployeeId) return 'Sign in to edit this task.';
    const names: string[] = [];
    if (t.responsiblePerson?.fullName) names.push(t.responsiblePerson.fullName);
    for (const s of t.otherStaff ?? []) if (s.fullName) names.push(s.fullName);
    if (names.length === 0) return 'No assignee on this task — assign someone in Settings to unlock it.';
    return `Only ${names.join(', ')} can edit this task.`;
  }

  const { data, isLoading, error } = useQuery<{ data: WizardState }>({
    queryKey: ['/onboard-assets/wizard-state', id],
    queryFn: async () => api<WizardState>(`/onboard-assets/${id}/wizard-state`) as any,
    enabled: !!id,
  });
  const state = data?.data;

  // Stepper labels = config stages + 'Finalize'. The hardcoded Stage 1
  // is gone — the equivalent "Asset Identity" stage now lives in the
  // config as real tasks (see seed-onboard-identity-tasks.ts).
  const finalizeStageName = 'Finalize';
  const allStages = React.useMemo<string[]>(() => {
    if (!state) return [finalizeStageName];
    return [...state.stages.map((s) => s.name), finalizeStageName];
  }, [state]);

  // Per-task answers, keyed by task _id. Mirrors the server's answers map and
  // lets the user edit prior stages. Pre-seeded from the matching
  // OnboardAsset.sourceField when no real answer exists yet — covers
  // in-flight records imported under the old built-in form flow.
  const [draftAnswers, setDraftAnswers] = React.useState<Record<string, any>>({});
  // Active step index — directly maps to allStages[].
  const [stepIndex, setStepIndex] = React.useState(0);
  const [maxReachedIndex, setMaxReachedIndex] = React.useState(0);
  // Latched while `handleContinue` is mid-save. Keeps the Continue button
  // disabled + "Saving…" for the whole batch instead of letting the
  // per-mutation `saveAnswer.isPending` flicker between sequential PUTs
  // (which used to make operators click multiple times). Declared up here
  // — must run on every render, even before the data-loaded early-return
  // guards, or React throws "Rendered more/fewer hooks than expected".
  const [isAdvancing, setIsAdvancing] = React.useState(false);
  // Seed local state from the server payload only on first arrival. After the
  // user clicks Continue we PUT and invalidate; the refetched payload still
  // reports currentStageIndex=-1 (no task answers yet), which used to snap
  // stepIndex back to 0 and made Continue look like a no-op.
  const initRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!state) return;
    if (initRef.current === id) return;
    initRef.current = id ?? null;
    const oa = state.onboardAsset;
    // Seed draft answers from server. For tasks with a `sourceField`,
    // back-fill from the matching OnboardAsset column when no real
    // answer exists yet — so existing records (imported under the old
    // BUILTIN_FIELDS form) keep their data visible in the new wizard.
    const seeded: Record<string, any> = { ...state.answers };
    for (const stage of state.stages) {
      for (const task of stage.tasks) {
        if (!task.sourceField) continue;
        if (seeded[task._id] !== undefined && seeded[task._id] !== null && seeded[task._id] !== '') continue;
        const oaVal = (oa as any)[task.sourceField];
        if (oaVal !== null && oaVal !== undefined && oaVal !== '') seeded[task._id] = oaVal;
      }
    }
    setDraftAnswers(seeded);
    // Server tells us where to land. If the record is already finalized,
    // jump straight to the Finalize step.
    if (oa.assetCreated || isFinalizedStatus(oa.status)) {
      const last = state.stages.length;
      setStepIndex(last);
      setMaxReachedIndex(last);
    } else {
      const start = state.isFallback ? 0 : Math.max(0, state.currentStageIndex);
      setStepIndex(start);
      setMaxReachedIndex(Math.max(maxReachedIndex, start));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // ---- Mutations ------------------------------------------------------------

  const saveAnswer = useMutation({
    mutationFn: async (p: { taskId: string; value: any }) => {
      // Use the dedicated upsert endpoint — it enforces that only the task's
      // assigned responsiblePerson / otherStaff can write, where the generic
      // CRUD POST does not.
      const r = await api(`/onboard-asset-task-answers/upsert`, {
        method: 'POST',
        body: JSON.stringify({ onboardAsset: id, task: p.taskId, value: p.value }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Save failed');
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const finalize = useMutation({
    mutationFn: async () => {
      const r = await api(`/onboard-assets/${id}/finalize`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Finalize failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Onboarding finalized', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/onboard-assets/wizard-state', id] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  if (isLoading || !state) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (error) {
    return <div className="p-8 text-destructive">Failed to load onboarding: {String((error as any).message ?? error)}</div>;
  }

  // ---- Step gating ----------------------------------------------------------

  function configStageOk(stageIdx: number): boolean {
    if (state!.isFallback) return true;
    const stage = state!.stages[stageIdx];
    if (!stage) return true;
    for (const t of stage.tasks) {
      if (!t.required) continue;
      const v = draftAnswers[t._id];
      const empty = v === undefined || v === null || v === '' || v === false;
      if (empty) return false;
    }
    return true;
  }

  // stepIndex maps directly into allStages[]; the last index is the
  // Finalize step.
  const isFinalizeStep = stepIndex === allStages.length - 1;
  const configStageIdx = stepIndex;
  const oa = state.onboardAsset;
  const finalized = oa.assetCreated || isFinalizedStatus(oa.status);
  // Any stage the user has already reached stays editable (not just the
  // furthest one), so assigned staff can go back and revise earlier answers
  // — e.g. fix Company/Site after the record has progressed. Still gated
  // per-task by `mine` (assignment) at the field level, and fully locked
  // once the onboarding is finalized.
  const currentStageEditable = !finalized && stepIndex <= maxReachedIndex;

  function canAdvance(): boolean {
    if (isFinalizeStep) return false;
    return configStageOk(configStageIdx);
  }

  async function handleContinue() {
    if (isAdvancing) return; // guard against double-clicks (flicker between sequential saves)
    setIsAdvancing(true);
    try {
      // Persist current stage's edits before advancing. Fire all saves in
      // parallel — sequential awaits used to cause the Continue button to
      // flicker between "Saving…" and "Continue →" between each PUT, and
      // operators reported needing multiple clicks to advance. Parallel
      // sends complete in one round-trip's worth of time and the button
      // stays "Saving…" continuously via the local `isAdvancing` flag.
      // Skip tasks the user isn't assigned to (server would 403) and
      // empty drafts (nothing to save).
      if (!state!.isFallback && configStageIdx >= 0 && configStageIdx < state!.stages.length) {
        const stage = state!.stages[configStageIdx];
        const sends = stage.tasks
          .filter((t) => isAssignedToMe(t))
          .filter((t) => draftAnswers[t._id] !== undefined)
          .map((t) => saveAnswer.mutateAsync({ taskId: t._id, value: draftAnswers[t._id] ?? null }));
        // Use allSettled so one task's permission/validation error doesn't
        // strand the others; individual errors surface as toasts via the
        // mutation's onError hook.
        await Promise.allSettled(sends);
      }
      const next = stepIndex + 1;
      setStepIndex(next);
      setMaxReachedIndex(Math.max(maxReachedIndex, next));
    } finally {
      setIsAdvancing(false);
    }
  }

  function handleJump(i: number) {
    if (i <= maxReachedIndex) setStepIndex(i);
  }

  // ---- Render ---------------------------------------------------------------

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/fleet-extras/onboard-assets"><ChevronLeft className="h-4 w-4" /> Back to onboardings</Link>
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">
          {state.isFallback ? (
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-3.5 w-3.5" /> No active onboarding configuration — showing fallback form.
            </span>
          ) : (
            <span>Configuration: <code className="text-foreground">{state.settingId?.slice(-6)}</code></span>
          )}
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">
          {oa.requestNumber ?? 'Onboarding'} — {oa.fleet ?? '(no fleet #)'}
        </h1>
        <p className="text-sm text-muted-foreground">
          Status: <span className="font-medium">{oa.status ?? 'Draft'}</span>
          {finalized && <span className="ml-2 text-emerald-600 dark:text-emerald-400">✓ Finalized</span>}
        </p>
      </div>

      <Card className="overflow-hidden">
        <Stepper
          steps={allStages}
          currentIndex={stepIndex}
          maxReachedIndex={maxReachedIndex}
          onJump={handleJump}
        />

        <CardContent className="p-6 space-y-4">
          {state.isFallback && !isFinalizeStep && (
            <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/40 p-4 text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-100">No active onboarding configuration.</p>
              <p className="text-amber-900/80 dark:text-amber-200/80 mt-1">
                Ask an administrator to open Settings → Asset Onboarding Configuration and tick "Apply",
                or run <code>npm run seed:onboard-identity-tasks -- --apply</code> to install the default
                Asset Identity tasks. The Finalize step at the end still works against the legacy fields.
              </p>
            </div>
          )}

          {!isFinalizeStep && !state.isFallback && (
            <div className="space-y-4">
              <h2 className="text-lg font-medium">{state.stages[configStageIdx]?.name}</h2>
              {state.stages[configStageIdx]?.tasks.length === 0 && (
                <p className="text-sm text-muted-foreground">No tasks configured in this stage.</p>
              )}
              <div className="space-y-3">
                {state.stages[configStageIdx]?.tasks.map((t) => {
                  const mine = isAssignedToMe(t);
                  return (
                    <Card key={t._id} className="border-border/60">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          {t.pointName ?? '(unnamed task)'}
                          {t.required && <span className="text-destructive text-xs">*</span>}
                        </CardTitle>
                        {t.pointDescription && (
                          <p className="text-xs text-muted-foreground">{t.pointDescription}</p>
                        )}
                      </CardHeader>
                      <CardContent className="pt-0 space-y-2">
                        <TaskField
                          task={t}
                          value={draftAnswers[t._id]}
                          onChange={(v) => setDraftAnswers((p) => ({ ...p, [t._id]: v }))}
                          // Auto-save on commit so progress persists without
                          // a Continue click — supports the multi-person
                          // fill-out flow. Guards on `mine` because the
                          // server gates upsert by assignee anyway.
                          onCommit={(v) => {
                            if (!currentStageEditable || !mine) return;
                            void saveAnswer.mutateAsync({ taskId: t._id, value: v ?? null });
                          }}
                          disabled={!currentStageEditable || !mine}
                        />
                        <TaskAssignment task={t} />
                        {!mine && (
                          <p className="text-xs text-muted-foreground italic pt-1">
                            {lockReasonFor(t)}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {isFinalizeStep && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium">Finalize onboarding</h2>
              <p className="text-sm text-muted-foreground">
                {finalized
                  ? 'This onboarding is already finalized. Use the back button to leave.'
                  : 'A new Asset will be created from the answers above. The IoT supplier (if any — pick via the GPS Type task) will be notified.'}
              </p>
              <Button
                disabled={finalized || finalize.isPending}
                onClick={() => finalize.mutate()}
              >
                {finalize.isPending ? 'Finalizing…' : finalized ? 'Already finalized' : 'Finalize onboarding'}
              </Button>
            </div>
          )}
        </CardContent>

        <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-6 py-3">
          <Button
            variant="outline"
            disabled={stepIndex === 0}
            onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
            title={stepIndex === 0 ? 'You are on the first stage' : 'Back to the previous stage'}
          >
            ← Back
          </Button>
          {/* Auto-save status — every field commit fires a save in the
              background, so the user can close the page or hand off to a
              colleague at any point. The indicator surfaces in-flight
              writes so a careful operator can wait for the green tick
              before navigating away. */}
          <div className="flex-1 text-center text-xs text-muted-foreground">
            {(saveAnswer.isPending || isAdvancing)
              ? <span className="text-amber-600 dark:text-amber-400">Saving…</span>
              : <span>✓ Progress saved automatically — safe to leave or hand off</span>}
          </div>
          {!isFinalizeStep && (
            <Button
              onClick={handleContinue}
              disabled={!canAdvance() || isAdvancing}
            >
              {isAdvancing ? 'Saving…' : 'Continue →'}
            </Button>
          )}
          {isFinalizeStep && (
            <Button variant="outline" onClick={() => navigate('/fleet-extras/onboard-assets')}>
              Done
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
